import React, { useEffect, useRef } from 'react'

interface PerceptionBarModalProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  initialFocusId?: string
  className?: string
  overlayClassName?: string
  allowNestedModals?: boolean
}

export const idPerceptionBarModal = 'perception-bar-modal'

const PerceptionBarModal: React.FC<PerceptionBarModalProps> = ({
  isOpen,
  onClose,
  children,
  initialFocusId,
  className = '',
  overlayClassName = '',
  allowNestedModals = false
}) => {
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  // Save previous focus and set initial focus
  useEffect(() => {
    if (isOpen) {
      // Save current focus
      previousFocusRef.current = document.activeElement as HTMLElement

      // Set focus on a specific element if provided
      if (initialFocusId != null && initialFocusId !== '') {
        setTimeout(() => {
          const elementToFocus = document.getElementById(initialFocusId)
          if (elementToFocus != null) {
            elementToFocus.focus()
          }
        }, 0)
      }
    } else if (previousFocusRef.current != null) {
      // Return focus when modal closes
      previousFocusRef.current.focus()
    }
  }, [isOpen, initialFocusId])

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        onClose()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleOverlayClick = (e: React.MouseEvent): void => {
    // If nested modals are allowed, only close if clicking directly on the overlay (not on child elements)
    if (allowNestedModals && e.target !== e.currentTarget) {
      return
    }
    onClose()
  }

  return (
    <div className={overlayClassName}>
      <button
        className='absolute inset-0 w-full h-full bg-transparent border-0 cursor-default'
        onClick={handleOverlayClick}
        aria-label='Close modal'
        aria-hidden
      />
      <div
        ref={modalRef}
        className={className}
        aria-labelledby='modal-title'
        data-testid={idPerceptionBarModal}
      >
        {children}
      </div>
    </div>
  )
}

export default PerceptionBarModal
